import { t, useLanguage } from "../../i18n";
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { publicAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Clock, Package, Truck, UtensilsCrossed } from 'lucide-react';
import { formatOrderNumber, getOrderItemCount } from '../../lib/utils';
const STATUS_LABELS = {
  get awaiting_payment() {
    return t("Paiement à finaliser");
  },
  get cancelled() {
    return t("Annulée");
  },
  get pending() {
    return t("En attente");
  },
  get preparing() {
    return t("En préparation");
  },
  get ready() {
    return t("Prête");
  },
  get delivered() {
    return t("Livrée");
  }
};
const STATUS_ICONS = {
  pending: Clock,
  preparing: UtensilsCrossed,
  ready: Package,
  delivered: Truck
};
const STATUS_CONFIG = {
  pending: {
    color: 'bg-blue-600',
    borderColor: 'border-l-blue-600',
    iconBg: 'bg-blue-100',
    iconText: 'text-blue-700',
    badgeClassName: 'border-blue-200 bg-blue-50 text-blue-700'
  },
  preparing: {
    color: 'bg-amber-500',
    borderColor: 'border-l-amber-500',
    iconBg: 'bg-amber-100',
    iconText: 'text-amber-700',
    badgeClassName: 'border-amber-200 bg-amber-50 text-amber-700'
  },
  ready: {
    color: 'bg-green-500',
    borderColor: 'border-l-green-500',
    iconBg: 'bg-green-100',
    iconText: 'text-green-700',
    badgeClassName: 'border-green-200 bg-green-50 text-green-700'
  },
  delivered: {
    color: 'bg-slate-400',
    borderColor: 'border-l-slate-400',
    iconBg: 'bg-slate-100',
    iconText: 'text-slate-600',
    badgeClassName: 'border-slate-200 bg-slate-50 text-slate-700'
  }
};
export default function MyOrders() {
  useLanguage();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const loadOrders = async () => {
      const stored = JSON.parse(localStorage.getItem('glassorder-active-orders') || '[]');
      if (!stored.length) {
        setOrders([]);
        setLoading(false);
        return;
      }
      try {
        const results = await Promise.allSettled(stored.map(entry => publicAPI.getOrder(entry.id)));
        const loadedOrders = results.map((result, index) => {
          if (result.status !== 'fulfilled') return null;
          const order = result.value?.data;
          if (!order) return null;
          return {
            ...order,
            stored_created_at: stored[index]?.created_at
          };
        }).filter(Boolean).sort((a, b) => new Date(b.created_at || b.stored_created_at || 0) - new Date(a.created_at || a.stored_created_at || 0));
        setOrders(loadedOrders);
        const stillRelevant = loadedOrders.slice(0, 30).map(order => ({
          id: order.id,
          table_number: order.table_number,
          establishment_id: order.establishment_id,
          created_at: order.created_at || new Date().toISOString()
        }));
        localStorage.setItem('glassorder-active-orders', JSON.stringify(stillRelevant));
      } catch (err) {
        console.error('Error loading my orders:', err);
      } finally {
        setLoading(false);
      }
    };
    loadOrders();
  }, []);
  return <div className="min-h-screen bg-slate-50">
      <div className="sticky top-0 z-20 border-b border-white/10 bg-gradient-to-r from-blue-950 via-blue-900 to-indigo-900 p-4 shadow-lg shadow-blue-950/10">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate('/menu')} className="rounded-full bg-white/10 p-2 text-white backdrop-blur transition-colors hover:bg-white/20" aria-label={t("Retour au menu")}>

            <ArrowLeft className="h-5 w-5" />
          </button>

          <div className="text-center">
            <p className="text-xs font-medium uppercase tracking-[0.18em] text-blue-100">
              GlassOrder
            </p>
            <h1 className="text-lg font-bold text-white" style={{
            fontFamily: 'Manrope'
          }}>{t("Mes commandes")}

            </h1>
          </div>

          <div className="w-9" />
        </div>
      </div>

      <div className="p-4 space-y-3">
        {loading ? <div className="rounded-2xl border border-slate-100 bg-white p-6 text-center text-slate-500 shadow-sm">{t("Chargement...")}

        </div> : orders.length === 0 ? <div className="rounded-3xl border border-slate-100 bg-white p-6 text-center shadow-sm">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 text-blue-700">
              <Package className="h-6 w-6" />
            </div>

            <p className="mb-1 font-semibold text-slate-900" style={{
          fontFamily: 'Manrope'
        }}>{t("Aucune commande en cours")}

          </p>

            <p className="mb-5 text-sm text-slate-500">{t("Vos commandes actives apparaîtront ici après validation.")}

          </p>

            <Button onClick={() => navigate('/menu')} className="rounded-full bg-gradient-to-r from-blue-950 via-blue-900 to-indigo-900 px-6 text-white shadow-md shadow-blue-950/20 hover:opacity-95">{t("Voir le menu")}

          </Button>
          </div> : orders.map(order => {
        const StatusIcon = STATUS_ICONS[order.status] || Clock;
        const config = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
        return <button key={order.id} onClick={() => navigate(`/order-confirmation?order_id=${order.id}`)} className={`w-full rounded-3xl border border-slate-100 border-l-4 ${config.borderColor} bg-white p-4 text-left shadow-sm transition-all active:scale-[0.99] hover:shadow-md`}>

                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <div className={`mt-1 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${config.iconBg} ${config.iconText}`}>

                      <span className={`h-3 w-3 rounded-full ${config.color}`} />
                    </div>

                    <div className="min-w-0">
                      <p className="font-semibold text-slate-950" style={{
                  fontFamily: 'Manrope'
                }}>{t("Commande")}
                      {formatOrderNumber(order)}
                      </p>

                      <p className="mt-1 text-sm text-slate-500">{t("Table")}
                      {order.table_number}
                      </p>

                      <p className="mt-1 text-xs text-slate-400">
                        {getOrderItemCount(order)}{' '}{t("article(s)")}
                    </p>
                    </div>
                  </div>

                  <Badge variant="outline" className={`flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 ${config.badgeClassName}`}>

                    <StatusIcon className="h-3.5 w-3.5" />
                    {STATUS_LABELS[order.status] || order.status}
                  </Badge>
                </div>
              </button>;
      })}
      </div>
    </div>;
}
